import { Link } from "@tanstack/react-router";
import { ArrowRight, ChevronDown } from "lucide-react";
import { useState } from "react";
import { Reveal } from "@/components/reveal";
import { buildFaqJsonLd, JsonLd } from "@/lib/json-ld";

export type FaqItemData = {
	question: string;
	answer: string;
};

const DEFAULT_FAQS: FaqItemData[] = [
	{
		question: "What if AgentReacher is not for me?",
		answer:
			"Subscribe and try it on real posts. If it is not a fit, email us within 30 days of your first payment and we refund it in full, no questions asked.",
	},
	{
		question: "Which AI assistants does AgentReacher work with?",
		answer:
			"AgentReacher connects to Claude, OpenClaw, ChatGPT, Cursor, Codex, and Gemini through MCP. You paste a short setup prompt or run the init command, and your assistant gains the tools to plan, schedule, and publish posts on your behalf.",
	},
	{
		question: "Does AgentReacher have an API & MCP?",
		answer:
			"Yes. AgentReacher is built around MCP, so any compatible AI assistant can plan, schedule, and publish through it. You can also generate API keys for programmatic access from your own tools and scripts.",
	},
	{
		question: "Does AgentReacher support post approvals?",
		answer:
			"Yes. AgentReacher has a built-in team approval flow on every plan, with per-workspace approval gates, reviewer roles, inline review from the calendar or post detail, comment threads, and an audit trail. You can see how it works on the team approval feature page.",
	},
	{
		question: "Does AgentReacher support direct posting to Instagram?",
		answer:
			"Yes. AgentReacher publishes single images, carousels, and video Reels directly to Instagram Business and Creator accounts. There is no manual notification step, so scheduled posts go live on their own.",
	},
	{
		question: "Does AgentReacher support social listening?",
		answer:
			"Not yet. AgentReacher is focused on planning, scheduling, and publishing posts through your AI assistant. Keyword monitoring and mention tracking are not part of the product today.",
	},
	{
		question: "Can I review captions before anything posts?",
		answer:
			"Yes. AgentReacher can draft platform-specific captions first, then you can edit the text, timing, media, and selected channels before a post is scheduled or published.",
	},
	{
		question: "Is AgentReacher approved by the social networks?",
		answer:
			"Yes. AgentReacher publishes through the official APIs of every supported platform: LinkedIn, Instagram, Facebook, X, TikTok, YouTube, Pinterest, Threads, and Bluesky. It goes through each platform's app review where required, so your connected accounts stay safe.",
	},
	{
		question: "What counts as a social account?",
		answer:
			"Each profile, page, or channel you connect counts as one social account. For example, two Instagram accounts, a LinkedIn page, and an X account would be four social accounts. The Pro plan includes 15 connected social accounts and the Max plan includes 150.",
	},
];

export function FaqSection({
	items = DEFAULT_FAQS,
}: {
	items?: FaqItemData[];
}) {
	const [openIndex, setOpenIndex] = useState(0);

	return (
		<section className="bg-border">
			<JsonLd data={buildFaqJsonLd(items)} />
			<div className="grid grid-cols-1 gap-px lg:grid-cols-3">
				<div className="homepage-hover-cell rounded-md bg-white dark:bg-black p-8 sm:p-12">
					<Reveal>
						<h2 className="text-3xl font-semibold tracking-[-0.04em] sm:text-4xl">
							Frequently
							<br />
							Asked Questions
						</h2>
						<p className="mt-6 max-w-xs text-base text-muted-foreground">
							Everything you need to know before you connect your first account.
						</p>
						<p className="mt-6 text-sm text-muted-foreground">
							Want to see it in action?
						</p>
						<Link
							to="/choose-plan"
							search={{ redirect: undefined, reason: undefined }}
							className="mt-3 inline-flex items-center gap-2 rounded-md bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
						>
							Start now
							<ArrowRight className="size-3.5" />
						</Link>
					</Reveal>
				</div>

				<div className="homepage-hover-cell rounded-md bg-white dark:bg-black p-4 sm:p-6 lg:col-span-2 lg:p-8">
					<Reveal delay={120}>
						<ul className="space-y-2">
							{items.map((item, i) => {
								const open = i === openIndex;
								return (
									<li key={item.question}>
										<div
											className={`rounded-xl bg-card transition-colors ${
												open
													? "shadow-[0_1px_0_0_rgba(0,0,0,0.04),0_8px_24px_-12px_rgba(15,10,30,0.18)] ring-1 ring-border"
													: ""
											}`}
										>
											<button
												type="button"
												onClick={() => setOpenIndex(open ? -1 : i)}
												aria-expanded={open}
												className="flex w-full items-start justify-between gap-4 rounded-xl px-5 py-4 text-left"
											>
												<span className="text-base font-medium text-foreground">
													{item.question}
												</span>
												<ChevronDown
													className={`mt-0.5 size-4 shrink-0 text-muted-foreground transition-transform ${
														open ? "rotate-180" : ""
													}`}
												/>
											</button>
											{open && (
												<div className="px-5 pb-4">
													<p className="text-sm leading-relaxed text-muted-foreground">
														{item.answer}
													</p>
												</div>
											)}
										</div>
										{!open && i < items.length - 1 && (
											<div className="mx-5 border-b border-border/60" />
										)}
									</li>
								);
							})}
						</ul>
					</Reveal>
				</div>
			</div>
		</section>
	);
}
